import React, { useState, useMemo } from 'react';
import { X, Search, Plus, Calendar, Clock, User, AlertCircle } from 'lucide-react';

/**
 * InsertProcessModal - Modal for inserting a process into an existing chain
 * Allows selecting an unscheduled process to insert between two chain items
 */
const InsertProcessModal = ({
  isOpen,
  onClose,
  onInsert,
  chainItems,
  insertIndex,
  availableProcesses,
  projectData,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProcess, setSelectedProcess] = useState(null);

  // Filter processes that are not in chains
  const unscheduledProcesses = useMemo(() => {
    return availableProcesses.filter(
      (process) => !process.chainId || !process.isChained
    );
  }, [availableProcesses]);

  // Filter by search term
  const filteredProcesses = useMemo(() => {
    if (!searchTerm) return unscheduledProcesses;

    const term = searchTerm.toLowerCase();
    return unscheduledProcesses.filter(
      (process) =>
        process.anr?.toLowerCase().includes(term) ||
        process.auftragsbezeichnung?.toLowerCase().includes(term) ||
        process.label?.toLowerCase().includes(term)
    );
  }, [unscheduledProcesses, searchTerm]);

  const handleInsert = () => {
    if (selectedProcess) {
      onInsert(selectedProcess);
      setSelectedProcess(null);
      setSearchTerm('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        <div className="relative w-full max-w-2xl transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all">
          {/* Header */}
          <div className="border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-white/10 p-2">
                  <Plus className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Insert Process into Chain</h2>
                  <p className="mt-1 text-sm text-blue-100">
                    Insert after position {insertIndex + 1}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-lg p-2 text-white hover:bg-white/10 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="border-b border-gray-200 p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search processes by ANR, description, or type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Process List */}
          <div className="max-h-96 overflow-y-auto p-4">
            {filteredProcesses.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="rounded-full bg-gray-100 p-4 mb-4">
                  <AlertCircle className="h-12 w-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">No Processes Found</h3>
                <p className="mt-2 text-sm text-gray-500 max-w-sm">
                  {searchTerm
                    ? 'Try adjusting your search terms'
                    : 'All processes are already in chains'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredProcesses.map((process) => (
                  <div
                    key={process.uniqueId}
                    onClick={() => setSelectedProcess(process)}
                    className={`cursor-pointer rounded-lg border-2 p-4 transition-all ${
                      selectedProcess?.uniqueId === process.uniqueId
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Profile Image */}
                      {process?.projectData?.profileImageUrl && (
                        <img
                          src={process.projectData.profileImageUrl}
                          alt="Profile"
                          className="h-10 w-10 rounded-full object-cover border-2 border-gray-200"
                        />
                      )}

                      {/* Process Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{process.anr}</h4>
                            <p className="mt-0.5 text-sm text-gray-600 truncate">
                              {process.auftragsbezeichnung || process.label}
                            </p>
                            {projectData?.[process.anr]?.projectDetails?.kunden?.bezeichnung && (
                              <p className="mt-0.5 text-xs text-gray-500">
                                {projectData[process.anr].projectDetails.kunden.bezeichnung}
                              </p>
                            )}
                          </div>

                          {selectedProcess?.uniqueId === process.uniqueId && (
                            <div className="flex-shrink-0">
                              <div className="rounded-full bg-blue-500 p-1">
                                <svg
                                  className="h-4 w-4 text-white"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                          {process.date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(process.date).toLocaleDateString('de-DE')}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {process.hours}h
                          </span>
                          {process.names && process.names.length > 0 && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {process.names.join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 bg-gray-50 p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-gray-600">
                <AlertCircle className="inline h-4 w-4 mr-1" />
                Process will be inserted and chain order will be updated automatically
              </div>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleInsert}
                  disabled={!selectedProcess}
                  className={`rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors ${
                    selectedProcess
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}
                >
                  Insert Process
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsertProcessModal;
